/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

export const attachmentTypes = {
  TECHNICAL: 'TECHNICAL',
  ADDITIONAL: 'ADDITIONAL',
  PAYMENT: 'PAYMENT',
  BIO_MATERIAL: 'BIO_MATERIAL',
  SEQ: 'SEQ',
  SPC: 'SPC',
  SA: 'SA',
  SIGNATURE: 'SIGNATURE',
};

export const specialDocumentTypes = {
  CLAIMS: 'CLAIMS',
  COMBINED: 'COMBINED',
  ABSTRACT: 'ABSTRACT',
  OTHER: 'OTHER',
  DRAWINGS: 'DRAWINGS',
  SEQ_LIST: 'SEQ_LIST',
  ADDITIONAL_EXAMINATION: 'SESFD-P-ADDINV',
  TRANSLATION_DRAWINGS: 'TRANSLATION_DRAWINGS',
  DIFFERENT_CLAIMS: 'DIFFERENT_CLAIMS',
  PRE_CONVERSION: 'PRE_CONVERSION',
};

// attachmentTypes.json configuration attributes
//  - used to set default values
//  - used to map reasonable names for the nonsense names the JSON config properties
export const ATTACHMENT_CONFIG_ATTRIBUTES = {
  ACCEPTED_MIME_TYPES: {
    name: 'acceptedMimeTypes',
    default: ['application/pdf'],
  },
  ACCEPTED_FILE_EXTENSIONS: {
    name: 'acceptedFileExtensions',
    default: ['pdf'],
  },
  DROPDOWN_DEFINITION_NAME: {
    name: 'dropdownDefinition',
    default: undefined,
  },
  HIDE_META_FIELDS: {
    name: 'hideAdditionalField',
    default: false,
  },
  META_FIELD_REQUIRED: {
    name: 'additionalFieldRequired',
    default: true,
  },
  MAX_NUMBER_OF_FILES: {
    name: 'maxNumberOfFiles',
    default: 999,
  },
  MAX_ONE_ATTACHMENT_PER_SUBTYPE: {
    name: 'maxOneAttachmentPerType',
    default: false,
  },
  // this is the same as the previous, but it's used with the legacy back-end code
  NEED_DISTINCT_TYPES: {
    name: 'needDistinctTypes',
    default: false,
  },

  NEED_NUMBER_OF_CLAIMS: {
    name: 'needNumberOfClaims',
    default: true,
  },

  MIN_NUMBER_OF_SUB_TYPES: {
    name: 'minNumberOfSubtypes',
    default: 2,
  },
};

export const ATTACHMENT_TYPE_META_FIELDS = {
  [attachmentTypes.TECHNICAL]: ['numberOfClaims', 'numberOfDrawings', 'figureToBePublished'],
  [attachmentTypes.ADDITIONAL]: ['documentType'],
  [attachmentTypes.SA]: ['numberOfNewClaims', 'totalNumberOfPreviouslyDeclaredClaims', 'numberOfClaims', 'numberOfAddinv'],
};

export const specialDocumentsInAnotherLanguage = {
  DESCRIPTION_IN_ANOTHER_LANGUAGE: 'DESCRIPTION_IN_ANOTHER_LANGUAGE',
  DRAWINGS_IN_ANOTHER_LANGUAGE: 'DRAWINGS_IN_ANOTHER_LANGUAGE',
  CLAIMS_IN_ANOTHER_LANGUAGE: 'CLAIMS_IN_ANOTHER_LANGUAGE',
  ABSTRACT_IN_ANOTHER_LANGUAGE: 'ABSTRACT_IN_ANOTHER_LANGUAGE',
};

export const translationDocuments = {
  TRANSLATION_DESCRIPTION: 'TRANSLATION_DESCRIPTION',
  TRANSLATION_CLAIMS: 'TRANSLATION_CLAIMS',
  TRANSLATION_ABSTRACT: 'TRANSLATION_ABSTRACT',
  TRANSLATION_DRAWINGS: 'TRANSLATION_DRAWINGS',
  TRANSLATION_DESCRIPTION_OTHER_LANGUAGE: 'TRANSLATION_DESCRIPTION_OTHER_LANGUAGE',
  TRANSLATION_CLAIMS_OTHER_LANGUAGE: 'TRANSLATION_CLAIMS_OTHER_LANGUAGE',
  TRANSLATION_ABSTRACT_OTHER_LANGUAGE: 'TRANSLATION_ABSTRACT_OTHER_LANGUAGE',
  TRANSLATION_DRAWINGS_OTHER_LANGUAGE: 'TRANSLATION_DRAWINGS_OTHER_LANGUAGE',
};

export const extraClaimsInfo = {
  EXTRAS: 'extraClaims',
};

export const paymentDocumentTypes = {
  PROOF_OF_PAYMENT: 'PROOF_OF_PAYMENT',
};

export const maxFileSize = 524288000;
export const MIME_TYPE_PDF = 'application/pdf';
export const acceptedMimeTypes = [MIME_TYPE_PDF];

export const maxCombinedFilesSize = '500Mb';
export const allowedTechFileFormats = ['PDF'];
export const allowedAddFileFormats = ['PDF'];
export const allowedBioFileFormats = [
  'PDF',
  'PNG',
  'JPG',
  'JPEG',
  'DOC',
  'DOCX',
];

export const selectLabel = (type, t) => {
  return t(`attachments.type.${type}`);
};

export const isCombinedFile = (file) => {
  return file.attachmentType === specialDocumentTypes.COMBINED;
};

export const hasFileName = (files, originalFileName) => {
  return !!files?.find(f => (f.originalFileName || f.name) === originalFileName);
};

export const omit = (obj, prop) => {
  const copyObj = { ...obj };
  delete copyObj[prop];
  return copyObj;
};

export const reachedUploadLimit = (files, file) => {
  const filesInTotal = files.reduce((acc, f) => acc + f.byteSize, 0);
  return maxFileSize < filesInTotal + file.byteSize;
};

/**
 * A function that checks if the attachment contains a type of document or not
 * @param {Object} attachment - an attachment object
 * @param {Object} type - the type to check
 * @returns {boolean} a truthy or falsy value
 */
export function hasType(attachment, type) {
  return (
    attachment.attachmentType === type ||
    (attachment &&
      attachment.combinedFileTypeScopes &&
      attachment.combinedFileTypeScopes.some(file => file.type.includes(type)))
  );
}

export function getCustomNumberOfClaims(attachment) {
  const excludedTypes = [specialDocumentTypes.CLAIMS, translationDocuments.TRANSLATION_CLAIMS];

  if (attachment?.numberOfClaims && !excludedTypes.includes(attachment?.attachmentType)) {
    return [
      {
        title: 'claims.number.colon',
        value: attachment?.numberOfClaims,
      },
    ];
  }

  if (attachment?.numberOfNewClaims) {
    return [
      {
        title: 'claims.new.number.colon',
        value: attachment?.numberOfNewClaims,
      },
      {
        title: 'claims.total.number.colon',
        value: attachment?.totalNumberOfPreviouslyDeclaredClaims,
      },
    ];
  }
  return null;
}

export function getCustomNumberOfAddinv(attachment) {
  if (attachment?.numberOfAddinv) {
    return [
      {
        title: 'addinv.number.colon',
        value: attachment?.numberOfAddinv,
      },
    ];
  }
  return null;
}

/**
 * A pure function that returns the number of claims in the attachment
 * if the attachment is not of type claims or combined file containing
 * claims it will return null
 * @param {Object} attachment
 * @returns {number} the number of claims in the attachment
 */
export function getNumberOfClaims(attachment) {
  return attachment.attachmentType.includes(specialDocumentTypes.CLAIMS) ||
    attachment.attachmentType.includes(translationDocuments.TRANSLATION_CLAIMS) ||
    attachment.attachmentType.includes(specialDocumentsInAnotherLanguage.CLAIMS_IN_ANOTHER_LANGUAGE)
    ? attachment.numberOfClaims || 0
    : attachment.combinedFileTypeScopes.find(file => file.type.includes(specialDocumentTypes.CLAIMS))?.numberOfClaims ||
        0;
}

/**
 * A pure function that returns the number of drawings in the attachment
 * if the attachment is not of type drawings or combined file containing
 * drawings it will return null
 * @param {Object} attachment
 * @returns {number} the number of drawings in the attachment
 */
export function getNumberOfDrawings(attachment) {
  return attachment.attachmentType.includes(specialDocumentTypes.DRAWINGS)
    ? attachment.numberOfDrawings
    : attachment.combinedFileTypeScopes.find(file => file.type.includes(specialDocumentTypes.DRAWINGS))
      ?.numberOfDrawings;
}

/**
 * A pure function that returns the figure to be published in the attachment
 * if the attachment is not of type abstract or combined file containing
 * abstract it will return null
 * @param {Object} attachment
 * @returns {number} the number of abstract in the attachment
 */
export function getFigureToBePublished(attachment) {
  return attachment.attachmentType.includes(specialDocumentTypes.ABSTRACT)
    ? attachment.figureToBePublished
    : attachment.combinedFileTypeScopes.find(file => file.type.includes(specialDocumentTypes.ABSTRACT))
      ?.figureToBePublished;
}

/**
 * A pure function that returns the document type in the attachment
 * if the attachment is not of type other or combined file containing
 * other types it will return null
 * @param {Object} attachment
 * @returns {type} the type of the document
 */
export function getDocumentType(attachment) {
  return attachment.attachmentType === specialDocumentTypes.OTHER
    ? attachment.documentType
    : attachment.combinedFileTypeScopes.find(file => file.type === specialDocumentTypes.OTHER)?.documentType;
}

/**
 * A pure function that returns the list of technical document types descriptions
 * if the technical document type is not combined file it will return null
 * @param {Object} attachment
 * @returns {Array} a list with the attached files type
 */
export function getAttachmentsTypesFromCombinedFile(attachment, t) {
  return !attachment || !attachment.combinedFileTypeScopes
    ? null
    : attachment.combinedFileTypeScopes.map(file => selectLabel(file.type, t));
}
